import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatApiErrorDetail } from '../../utils/axios';
import { AdminSidebar } from './AdminLeaderboardWinners';
import { LogoUploader } from '../../components/LogoUploader';
import { Plus, Edit2, Trash2, X, Save, Users, RefreshCw } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: '', logo: '', website_url: '', description: '', category: 'sponsor', display_order: 0, status: 'active' };

export const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/partners`, { withCredentials: true });
      setPartners(data);
    } catch (e) {
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || '', logo: p.logo || '', website_url: p.website_url || '',
      description: p.description || '', category: p.category || 'sponsor',
      display_order: p.display_order || 0, status: p.status || 'active',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Nama partner wajib diisi'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_BASE}/admin/partners/${editing}`, form, { withCredentials: true });
        toast.success('Partner diperbarui');
      } else {
        await axios.post(`${API_BASE}/admin/partners`, form, { withCredentials: true });
        toast.success('Partner dibuat');
      }
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus partner ini? (Website mirror dengan nama sama juga ikut terhapus)')) return;
    try {
      await axios.delete(`${API_BASE}/admin/partners/${id}`, { withCredentials: true });
      toast.success('Partner dihapus');
      fetchAll();
    } catch (e) {
      toast.error('Gagal menghapus');
    }
  };

  const handleSyncCleanup = async () => {
    if (!window.confirm('Sinkronkan sekarang? Ini akan (1) hapus DUPLIKAT dengan nama sama (simpan yang paling lama), (2) buat mirror partner↔website yang hilang. Item unik tidak akan dihapus.')) return;
    try {
      const { data } = await axios.post(`${API_BASE}/admin/partners/sync-cleanup`, {}, { withCredentials: true });
      const dupP = data.removed_partner_duplicates || 0;
      const dupW = data.removed_website_duplicates || 0;
      const created = (data.created_websites || 0) + (data.created_partners || 0);
      const msg = [];
      if (dupP || dupW) msg.push(`${dupP + dupW} duplikat dihapus`);
      if (created) msg.push(`${created} mirror dibuat`);
      toast.success(msg.length ? `Selesai — ${msg.join(', ')}` : 'Semua sudah sinkron, tidak ada perubahan');
      fetchAll();
    } catch (e) {
      toast.error('Gagal sinkronisasi');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1020]" data-testid="admin-partners-page">
      <AdminSidebar active="/admin/partners" />

      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white mb-2 flex items-center gap-3">
              <Users className="text-[#FFD60A]" size={36} /> Partner / Website
            </h1>
            <p className="text-gray-400">Kelola daftar partner + website prediksi (satu isian untuk keduanya)</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSyncCleanup} className="btn-secondary flex items-center gap-2" data-testid="sync-cleanup-btn" title="Bersihkan orphan & mirror ulang partner ↔ website">
              <RefreshCw size={18} /> Sinkronkan
            </button>
            <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-partner-btn">
              <Plus size={18} /> Tambah Partner
            </button>
          </div>
        </div>

        <div className="glass-card p-6">
          {loading ? (
            <div className="text-gray-400">Loading...</div>
          ) : partners.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Belum ada partner</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {partners.map((p) => (
                <div key={p.id} className="glass-card p-4 bg-white/5" data-testid={`admin-partner-${p.id}`}>
                  {p.logo ? (
                    <img src={p.logo} alt={p.name} className="h-16 w-full object-contain mb-3" />
                  ) : (
                    <div className="h-16 w-full flex items-center justify-center bg-white/5 rounded mb-3">
                      <span className="text-white font-bold text-lg">{p.name?.[0]}</span>
                    </div>
                  )}
                  <div className="font-bold text-white mb-1">{p.name}</div>
                  <div className="text-xs text-gray-400 mb-2">
                    {p.category} · Order: {p.display_order} · <span className={p.status === 'active' ? 'text-green-400' : 'text-gray-500'}>{p.status}</span>
                  </div>
                  {p.description && <div className="text-xs text-gray-500 mb-3 line-clamp-2">{p.description}</div>}
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(p)} className="flex-1 px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-blue-400 text-xs flex items-center justify-center gap-1">
                      <Edit2 size={12} /> Edit
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="flex-1 px-2 py-1 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs flex items-center justify-center gap-1">
                      <Trash2 size={12} /> Hapus
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black text-white">{editing ? 'Edit' : 'Tambah'} Partner</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X size={24} /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Nama Partner *</label>
                  <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Nama partner" data-testid="form-partner-name" />
                </div>
                <LogoUploader
                  value={form.logo}
                  onChange={(url) => setForm({...form, logo: url})}
                  folder="partners"
                  label="Logo Partner"
                  dataTestId="partner-logo"
                />
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Website URL</label>
                  <input value={form.website_url} onChange={(e) => setForm({...form, website_url: e.target.value})} placeholder="https://partner.com" />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Deskripsi</label>
                  <textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Deskripsi partner" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Kategori</label>
                    <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
                      <option value="sponsor">Sponsor</option>
                      <option value="media">Media</option>
                      <option value="gaming">Gaming</option>
                      <option value="community">Community</option>
                      <option value="official">Official Partner</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Urutan</label>
                    <input type="number" value={form.display_order} onChange={(e) => setForm({...form, display_order: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Status</label>
                    <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                      <option value="active">Aktif</option>
                      <option value="inactive">Tidak Aktif</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-partner-btn">
                    <Save size={18} /> {saving ? 'Menyimpan...' : 'Simpan'}
                  </button>
                  <button onClick={() => setShowForm(false)} className="btn-secondary">Batal</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
